import * as R from 'ramda'
import { equals, searchObject, SetState } from '@unimpaired/utils'
import React, { Children, useState } from 'react'
import { ListQueryProps, Selection, SelectVariant } from '../Select'
import { Option } from './Option/Option'
import { Button } from '../../../Button/Button/Button'
import { SliderAnimation } from '../../../Animations'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { twMerge } from 'tailwind-merge'

type OptionsListProps = {
  searchTerm: string
  selections: Selection<any>[]
  handleSelect: (selections: Selection<any>) => void
  children: React.FunctionComponentElement<any>[]
  multiple?: boolean
  setSelectOpen?: (open: boolean) => void
  listQueryProps?: ListQueryProps
  variant?: SelectVariant
}

export const OptionsList = (props: OptionsListProps) => {
  const [slide, setSlide] = useState(0)
  const [label, setLabel] = useState<string>()

  const hasNested = Children.toArray(props.children).some(
    // @ts-expect-error
    (child) => child.props.nested,
  )

  // This might not be needed, but I worry that the added <SliderAnimation />
  // will cause unintended effects.  So, since we aren't using the nested values much yet,
  // I'm adding this code to effectively keep the addition of nested selections absolutely backwards compatible
  if (!hasNested)
    return (
      <React.Fragment>
        {Children.map(props.children, (child, i) =>
          searchObject(
            { v: child.props.value, l: child.props.label },
            props.searchTerm,
          ) ? (
            <OptionsListItem {...props} key={child.key || i} child={child} />
          ) : null,
        )}
      </React.Fragment>
    )

  return (
    <React.Fragment>
      <SliderAnimation selectedSlide={slide} order={0}>
        {Children.map(props.children, (child, i) =>
          searchObject(
            { v: child.props.value, l: child.props.label },
            props.searchTerm,
          ) ? (
            child.props.nested ? (
              <NestedOptionsListButton
                label={child.props.label}
                setLabel={setLabel}
                setSlide={setSlide}
              />
            ) : (
              <OptionsListItem {...props} key={child.key || i} child={child} />
            )
          ) : null,
        )}
      </SliderAnimation>

      <SliderAnimation selectedSlide={slide} order={1}>
        <Button
          variant='modal-button'
          iconPosition='left'
          type='button'
          icon={faChevronLeft}
          onClick={() => {
            setSlide(R.dec)
            setLabel(undefined)
          }}>
          Back
        </Button>
        {Children.map(props.children, (child, i) =>
          child.props.label === label ? (
            <OptionsList {...props}>{child.props.children}</OptionsList>
          ) : null,
        )}
      </SliderAnimation>
    </React.Fragment>
  )
}

const NestedOptionsListButton = (props: {
  label: string
  setLabel: SetState<string | undefined>
  setSlide: SetState<number>
}) => {
  const { label, setLabel, setSlide } = props

  return (
    <Button
      variant='unstyled'
      className={twMerge(
        'text-base-800 space-x-4 ml-4 focus:bg-blue-200 hover:text-blue-800 hover:bg-blue-200   focus:outline-none max-w-full min-w-0 focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5 block w-full px-3 pr-1 py-2 text-left placeholder-gray-400 truncate transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md appearance-none cursor-pointer overflow-y-auto sm:overflow-hidden disabled:bg-base-200 disabled:cursor-not-allowed dark:bg-white/10 dark:border-none dark:text-white border-none font-normal shadow-none',
      )}
      type='button'
      icon={faChevronRight}
      iconPosition='right'
      onClick={() => {
        setSlide(R.inc)
        setLabel(label)
      }}>
      {label}
    </Button>
  )
}

const OptionsListItem = ({
  child,
  ...props
}: OptionsListProps & {
  child: { props: any }
}) => {
  const isSelected = !!props.selections.find((option) =>
    equals(option.value, child.props.value),
  )

  const getRole = () => {
    if (props.multiple) {
      if (props.variant === 'switch-group') return 'switch'
      return 'checkbox'
    }

    return 'radio'
  }

  return (
    <Option
      setSelectOpen={props.setSelectOpen}
      selected={isSelected}
      onSelect={() =>
        props.handleSelect({
          value: child.props.value,
          label: child.props.label,
        })
      }
      onClick={() =>
        props.handleSelect({
          value: child.props.value,
          label: child.props.label,
        })
      }
      {...child.props}
      variant={props.variant}
      role={getRole()}
    />
  )
}
